import { useParams } from 'react-router-dom';

import { Activity, ActivityType, TreeActivity } from '@/features/course/admin/types/activity';
import {
  useLearningJourneyBreadcrumbs,
  useParentJourneyIdFromSearchParams,
} from '@/store/v2/journeys/useLearningJourneyBreadcrumbs';
import { ViewLayoutV2 } from '@/administration/pages/Course/MainLayoutView/ViewLayoutV2';
import { ViewActivityContentV2 } from '@/administration/pages/Course/MainLayoutView/ViewActivityContentV2';
import { ActivityId } from '@/administration/pages/Course/store/types';
import { useLearnerCourseNavigate } from '@/features/course/learner/hooks/useLearnerCourseNavigate';
import { useJourneyNavigate } from '@/administration/pages/Journey/utils/useJourneyNavigate';

type ModularLearningDetailsV2Props = {
  title?: string;
  urn?: string;
  spaceId?: number;
  loading?: boolean;
  learningId?: number;
  areAllActivitiesCompleted: boolean;
  activities: Activity[];
  isDrawerOpen: boolean;
  activitiesTotalCount: number;
  activityActiveCount: number;
  previousActiveActivityIndex: number;
  isSavedToMyLearning?: boolean;
  onNewActivityClick: (activityNumber: number) => void;
  onUpNavigationClick: () => void;
  onDownNavigationClick: () => void;
  onSaveClick?: () => void;
  onGoBack?: () => void;
  onSetDrawerOpen: (value: boolean) => void;
  onSetActivityActive: (activityId: ActivityId) => void;
  onActivityChange: (id: ActivityId, activity: Partial<Activity>) => void;
  treeActivities: TreeActivity[];
};

export const ModularLearningDetailsV2 = ({
  title,
  urn,
  spaceId,
  loading,
  learningId,
  activities,
  isDrawerOpen,
  activitiesTotalCount,
  activityActiveCount,
  previousActiveActivityIndex,
  isSavedToMyLearning,
  onNewActivityClick,
  onUpNavigationClick,
  onDownNavigationClick,
  onSaveClick,
  onGoBack,
  onSetDrawerOpen,
  areAllActivitiesCompleted,
  onSetActivityActive,
  onActivityChange,
  treeActivities,
}: ModularLearningDetailsV2Props): JSX.Element => {
  const { navigateToLearnerView } = useJourneyNavigate();
  const { navigateToCourseStart } = useLearnerCourseNavigate();

  const { id } = useParams<{ id: string }>();
  const parsedCourseId = id ? parseInt(id) : NaN;

  const parentJourneyIdFromSearchParams = useParentJourneyIdFromSearchParams();
  const parentJourney = useLearningJourneyBreadcrumbs({
    id: parsedCourseId,
  });

  const handleGoBack = () => {
    if (
      // We SPECIFICALLY check that the search params has parent journey id.
      // We ignore all other cases (when course has parent but was open not from journey).
      parentJourneyIdFromSearchParams &&
      parentJourney?.journeyId &&
      parentJourney.journeyTitle
    ) {
      // If the modular learning has parent journey AND was open via journey page,
      // in this case we make close button behave as a link to that journey.
      navigateToLearnerView({ journeyId: parentJourney.journeyId, journeyTitle: parentJourney.journeyTitle });
    } else {
      navigateToCourseStart({ id: learningId || 0, title: title || '' });
    }

    if (onGoBack) {
      onGoBack();
    }
  };

  const handleStartPageClick = () => {
    navigateToCourseStart({ id: learningId || 0, title: title || '' });

    if (onGoBack) {
      onGoBack();
    }
  };

  return (
    <>
      {!!loading || !spaceId || !learningId ? (
        <ViewLayoutV2.LoadingState />
      ) : (
        <ViewLayoutV2
          title={title || ''}
          urn={urn || ''}
          areAllActivitiesCompleted={areAllActivitiesCompleted}
          content={activities.map((activity) => (
            <ViewActivityContentV2
              key={`activity-${activity.id}`}
              activity={activity}
              onActivityChange={onActivityChange}
              spaceId={spaceId}
            />
          ))}
          drawerItems={treeActivities.map((activity: TreeActivity) => ({
            ...activity,
            id: activity?.id as unknown as ActivityId,
            type: activity?.type as unknown as ActivityType,
            onSelect: (activityId: ActivityId) => onSetActivityActive(activityId),
          }))}
          onShowHideDrawerClick={() => onSetDrawerOpen(!isDrawerOpen)}
          onAddFavoritesClick={onSaveClick}
          onCloseClick={handleGoBack}
          onNewActivityClick={onNewActivityClick}
          onUpNavigationClick={onUpNavigationClick}
          onDownNavigationClick={onDownNavigationClick}
          onStartPageClick={handleStartPageClick}
          isDrawerOpen={isDrawerOpen}
          progress={{ value: activityActiveCount, maxValue: activitiesTotalCount - 1, prevValue: previousActiveActivityIndex }}
          isSavedToMyLearning={isSavedToMyLearning}
          activityActiveCount={activityActiveCount}
          activitiesTotalCount={activitiesTotalCount}
        />
      )}
    </>
  );
};
